import { log10Rational } from "@/lib/tool-kit/arithmetic/power";
import { BIG_ONE, BIG_ZERO } from "@/lib/tool-kit/math/bigint";
import { factorize, MAX_FACTOR_INPUT } from "@/lib/tool-kit/math/primes";
import {
  makeRational,
  parseRational,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/**
 * Logarithms: exact when the answer is a fraction, otherwise to double precision.
 *
 * log_b(x) is rational exactly when the prime exponents of x are a rational multiple of
 * those of b (log_4 8 = 3/2 because 8 = 2³ and 4 = 2²), which is checked on the
 * factorisations rather than guessed from a floating-point quotient. Everything else is
 * ln x / ln b in IEEE 754 doubles — about 15 significant digits, which is stated.
 */
export interface Operand {
  /** The value as an exact rational; null for Euler's number. */
  rational: Rational | null;
  isE: boolean;
  /** As the reader typed it, tidied. */
  text: string;
}

const E_TOKEN = "e";

/** A number, a fraction, or the letter e for Euler's number. */
export function parseOperand(raw: string): Operand | null {
  const text = raw.trim();
  if (text.toLowerCase() === E_TOKEN) {
    return { rational: null, isE: true, text: E_TOKEN };
  }
  const rational = parseRational(text);
  if (rational === null) {
    return null;
  }
  return { rational, isE: false, text: text.replace("-", "−") };
}

/** The natural logarithm of a positive operand, or null when it is not positive. */
export function naturalLog(op: Operand): number | null {
  if (op.isE) {
    return 1;
  }
  const value = op.rational;
  if (value === null || value.num <= BIG_ZERO) {
    return null;
  }
  const top = Number(value.num);
  const bottom = Number(value.den);
  if (Number.isFinite(top) && Number.isFinite(bottom)) {
    // Both parts are exact doubles when they are short, so one division is accurate
    // even for a number as close to 1 as 1.0000001.
    const ratio = top / bottom;
    if (ratio > 0 && Number.isFinite(ratio)) {
      return Math.log(ratio);
    }
  }
  return log10Rational(value) * Math.LN10;
}

/** Prime → exponent (signed) for a positive rational, or null if too large to factor. */
function primeVector(value: Rational): Map<bigint, number> | null {
  if (value.num > MAX_FACTOR_INPUT || value.den > MAX_FACTOR_INPUT) {
    return null;
  }
  const map = new Map<bigint, number>();
  const top = factorize(value.num);
  const bottom = factorize(value.den);
  if (!(top.complete && bottom.complete)) {
    return null;
  }
  for (const { prime, exponent } of top.factors) {
    map.set(prime, exponent);
  }
  for (const { prime, exponent } of bottom.factors) {
    map.set(prime, (map.get(prime) ?? 0) - exponent);
  }
  return map;
}

/** log_b x as an exact fraction when x is a rational power of b, else null. */
export function exactLogarithm(base: Rational, x: Rational): Rational | null {
  if (x.num === BIG_ONE && x.den === BIG_ONE) {
    return makeRational(BIG_ZERO);
  }
  const baseVector = primeVector(base);
  const xVector = primeVector(x);
  if (baseVector === null || xVector === null) {
    return null;
  }
  const primes = new Set([...baseVector.keys(), ...xVector.keys()]);
  let ratio: Rational | null = null;
  for (const prime of primes) {
    const b = baseVector.get(prime) ?? 0;
    const v = xVector.get(prime) ?? 0;
    if (b === 0) {
      if (v !== 0) {
        return null;
      }
      continue;
    }
    const candidate = makeRational(BigInt(v), BigInt(b));
    if (ratio === null) {
      ratio = candidate;
    } else if (ratio.num !== candidate.num || ratio.den !== candidate.den) {
      return null;
    }
  }
  return ratio;
}

export type LogError = "base-invalid" | "x-nonpositive" | "base-one";

export interface LogResult {
  /** log_b x as a double. */
  value: number;
  exact: Rational | null;
  lnX: number;
  lnBase: number;
  log10X: number;
  log2X: number;
  /** b raised to the computed value, to compare with x. */
  roundTrip: number | null;
}

export function computeLogarithm(
  base: Operand,
  x: Operand
): LogResult | { error: LogError } {
  const lnBase = naturalLog(base);
  if (lnBase === null) {
    return { error: "base-invalid" };
  }
  if (lnBase === 0) {
    return { error: "base-one" };
  }
  const lnX = naturalLog(x);
  if (lnX === null) {
    return { error: "x-nonpositive" };
  }
  let exact: Rational | null = null;
  if (base.rational && x.rational) {
    exact = exactLogarithm(base.rational, x.rational);
  } else if (
    x.rational &&
    base.isE &&
    x.rational.num === BIG_ONE &&
    x.rational.den === BIG_ONE
  ) {
    exact = makeRational(BIG_ZERO);
  } else if (x.isE && base.isE) {
    exact = makeRational(BIG_ONE);
  }
  let value = lnX / lnBase;
  if (exact) {
    value = Number(exact.num) / Number(exact.den);
  }
  const log10X = lnX / Math.LN10;
  return {
    value,
    exact,
    lnX,
    lnBase,
    log10X,
    log2X: lnX / Math.LN2,
    roundTrip: roundTrip(lnBase, value),
  };
}

function roundTrip(lnBase: number, value: number): number | null {
  const back = Math.exp(lnBase * value);
  return Number.isFinite(back) ? back : null;
}

/* ---------------------------------------------------------------- notation */

const SUBSCRIPT_DIGITS = "₀₁₂₃₄₅₆₇₈₉";
const WHOLE_NUMBER = /^\d{1,4}$/;

/** "log₁₀", "log₂", "ln", or "log_(2.5)" — the name of the logarithm to a base. */
export function logName(base: Operand): string {
  if (base.isE) {
    return "ln";
  }
  if (WHOLE_NUMBER.test(base.text)) {
    return `log${[...base.text].map((d) => SUBSCRIPT_DIGITS[Number(d)]).join("")}`;
  }
  return `log_(${base.text})`;
}
